import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Loader2, CheckCircle2, XCircle, User as UserIcon, RefreshCw,
  Package, IndianRupee, Truck, Mail, Phone, MapPin, ClipboardList, ArrowRight, ExternalLink, Ban,
  FileDown, Globe, Clock, TrendingUp, Building2,
  Headphones, Calendar as CalendarIcon, Send, MessageSquare, Search, X,
} from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popVerificationQueue, popVerifyUser, popSetRole, ROLES, roleLabel,
  popListBookOrders, popUpdateBookOrder,
  popMBAListProspectusDownloads,
  popListAdvisorBookings, popUpdateAdvisorBookingStatus,
} from '../../lib/popApi';

// -------------------------------------------------------------------
// Verification queue view
// -------------------------------------------------------------------
const VerificationView = () => {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setBusy(true); setMsg('');
    try { const r = await popVerificationQueue(); setQueue(r.results || []); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Could not load queue'); setQueue([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (uid, status) => {
    try { await popVerifyUser(uid, status); await load(); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Action failed'); }
  };
  const changeRole = async (uid, role) => {
    try { await popSetRole(uid, role); await load(); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Role update failed'); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/60">Review incoming profiles across universities, industry experts and faculty.</p>
        <button onClick={load} data-testid="admin-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/70">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      {msg && <div className="text-xs text-rose-300 mb-3">{msg}</div>}
      {busy ? (
        <div className="flex items-center justify-center py-20 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : queue.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
          <ShieldCheck className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <div className="text-sm text-white/70">Queue is empty. All caught up ✨</div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-queue-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-2.5">User</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Institution / Org</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {queue.map((p) => (
                <tr key={p.user_id} className="hover:bg-white/[0.02]" data-testid={`admin-row-${p.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {p.picture
                        ? <img src={p.picture} alt="" className="w-7 h-7 rounded-full ring-1 ring-white/20" />
                        : <div className="w-7 h-7 rounded-full bg-amber-300/20 text-amber-200 flex items-center justify-center text-xs font-bold"><UserIcon className="w-3 h-3" /></div>}
                      <div>
                        <div className="text-white/90 font-medium">{p.name}</div>
                        <div className="text-[10px] text-white/50">{p.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select value={p.role} onChange={(e) => changeRole(p.user_id, e.target.value)} data-testid={`admin-role-${p.user_id}`}
                      className="text-xs bg-white/5 border border-white/10 rounded px-2 py-1 text-white">
                      {ROLES.map((r) => (<option key={r.id} value={r.id} className="bg-slate-900">{roleLabel(r.id)}</option>))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-white/70">{p.institution || p.industry_org_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                      p.verification_status === 'pending' ? 'bg-amber-500/15 text-amber-300' :
                      p.verification_status === 'unverified' ? 'bg-white/10 text-white/70' :
                      'bg-emerald-500/15 text-emerald-300'
                    }`}>{p.verification_status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => act(p.user_id, 'verified')} data-testid={`admin-approve-${p.user_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Verify
                      </button>
                      <button onClick={() => act(p.user_id, 'rejected')} data-testid={`admin-reject-${p.user_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 text-xs font-semibold">
                        <XCircle className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// -------------------------------------------------------------------
// Book Orders view — SAMANVAI direct-buy pipeline
// -------------------------------------------------------------------
const STATUS_META = {
  payment_pending: { label: 'Payment pending', color: 'text-amber-300',   bg: 'bg-amber-500/15',   border: 'border-amber-400/30',   next: 'paid' },
  paid:            { label: 'Paid',            color: 'text-cyan-300',    bg: 'bg-cyan-500/15',    border: 'border-cyan-400/30',    next: 'shipped' },
  shipped:         { label: 'Shipped',         color: 'text-blue-300',    bg: 'bg-blue-500/15',    border: 'border-blue-400/30',    next: 'delivered' },
  delivered:       { label: 'Delivered',       color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-400/30', next: null },
  cancelled:       { label: 'Cancelled',       color: 'text-white/50',    bg: 'bg-white/10',       border: 'border-white/15',       next: null },
};

const OrderCard = ({ o, onAdvance, onCancel, busy, highlight }) => {
  const meta = STATUS_META[o.status] || STATUS_META.payment_pending;
  const price = new Intl.NumberFormat(undefined, { style: 'currency', currency: o.currency || 'INR', maximumFractionDigits: 0 }).format(o.amount_paid || 0);
  return (
    <div className={`rounded-lg border ${meta.border} bg-slate-950 p-3 ${highlight ? 'ring-1 ring-amber-300/40 shadow-[0_0_0_1px_rgba(252,211,77,0.15)]' : ''}`} data-testid={`order-card-${o.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] uppercase tracking-wider font-bold text-white/50">#{o.id.slice(0, 8).toUpperCase()}</span>
        <span className="text-amber-300 text-xs font-bold tabular-nums">{price}</span>
      </div>
      <div className="text-[13px] font-serif font-semibold text-white leading-tight truncate">{o.book_title}</div>
      <div className="text-[10px] text-white/50 truncate">by {o.book_author_name}</div>

      <div className="mt-2 space-y-0.5">
        <div className="text-[11px] text-white/85 truncate">{o.buyer_name}</div>
        <div className="text-[10px] text-white/50 truncate inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {o.buyer_email}</div>
        {o.buyer_phone && <div className="text-[10px] text-white/50 truncate inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {o.buyer_phone}</div>}
        {o.txn_ref && <div className="text-[10px] text-white/60 truncate">UPI: <span className="font-mono text-emerald-200">{o.txn_ref}</span></div>}
      </div>

      <details className="mt-2 group">
        <summary className="text-[10px] text-white/50 cursor-pointer hover:text-white/80 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Shipping details
        </summary>
        <div className="mt-1.5 text-[11px] text-white/70 leading-relaxed whitespace-pre-line rounded bg-white/[0.02] border border-white/5 p-2">
          {o.shipping_address}
          {(o.city || o.state) && <><br />{[o.city, o.state, o.pin].filter(Boolean).join(', ')}</>}
          {o.country && <><br />{o.country}</>}
        </div>
        {o.notes && <div className="mt-1.5 text-[10px] italic text-white/60">&ldquo;{o.notes}&rdquo;</div>}
      </details>

      <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-white/5">
        {meta.next && (
          <button onClick={() => onAdvance(o.id, meta.next)} disabled={busy}
            data-testid={`order-advance-${o.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] font-semibold bg-amber-300 hover:bg-amber-200 text-slate-900">
            Move to {STATUS_META[meta.next].label} <ArrowRight className="w-3 h-3" />
          </button>
        )}
        {['payment_pending', 'paid'].includes(o.status) && (
          <button onClick={() => onCancel(o.id)} disabled={busy}
            data-testid={`order-cancel-${o.id}`}
            title="Cancel order"
            className="inline-flex items-center justify-center px-2 py-1.5 rounded bg-white/5 hover:bg-rose-500/10 border border-white/10 text-white/60 hover:text-rose-300">
            <Ban className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

const OrdersView = () => {
  const [buckets, setBuckets] = useState({ payment_pending: [], paid: [], shipped: [], delivered: [], cancelled: [] });
  const [busy, setBusy] = useState(true);
  const [pending, setPending] = useState(false);
  const [msg, setMsg] = useState('');
  const [query, setQuery] = useState('');

  const load = async () => {
    setBusy(true); setMsg('');
    try { const r = await popListBookOrders(); setBuckets(r.buckets || {}); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Could not load orders'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const advance = async (id, nextStatus, noteOverride) => {
    setPending(true); setMsg('');
    try {
      let note = typeof noteOverride === 'string' ? noteOverride : '';
      // When marking as SHIPPED, prompt the admin for a tracking / AWB number so
      // the automated buyer email can include it. Same pattern for cancelled.
      if (!noteOverride && nextStatus === 'shipped') {
        const t = window.prompt(
          'Optional — enter courier + tracking / AWB number for the buyer email\n' +
          '(examples: "Delhivery AWB 1234567890", "Bluedart 900123456", "India Post EG1234567IN"):',
          ''
        );
        // User pressed Cancel → abort the transition entirely
        if (t === null) { setPending(false); return; }
        note = t.trim();
      }
      if (!noteOverride && nextStatus === 'cancelled') {
        const t = window.prompt('Optional — reason to include in the buyer notification:', '');
        if (t === null) { setPending(false); return; }
        note = t.trim();
      }
      await popUpdateBookOrder(id, nextStatus, note);
      await load();
    }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Could not update'); }
    finally { setPending(false); }
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this order? The buyer will be notified automatically.')) return;
    await advance(id, 'cancelled');
  };

  // ---------- Client-side search across every buyer/payment/order field ----------
  const q = query.trim().toLowerCase();
  const matches = (o) => {
    if (!q) return true;
    const hay = [
      o.id, o.book_title, o.book_author_name,
      o.buyer_name, o.buyer_email, o.buyer_phone,
      o.txn_ref, o.shipping_address, o.city, o.state, o.pin, o.country,
      o.notes,
      ...(o.admin_notes || []).map((n) => (typeof n === 'string' ? n : (n?.note || ''))),
    ].filter(Boolean).map(String).map((s) => s.toLowerCase());
    return hay.some((s) => s.includes(q));
  };
  const filteredBuckets = Object.fromEntries(
    Object.entries(buckets).map(([k, arr]) => [k, (arr || []).filter(matches)])
  );
  const totalRaw = Object.values(buckets).reduce((s, arr) => s + (arr?.length || 0), 0);
  const total = Object.values(filteredBuckets).reduce((s, arr) => s + (arr?.length || 0), 0);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/60">SAMANVAI direct-buy orders — advance the pipeline as you receive payment and dispatch books.</p>
        <button onClick={load} data-testid="orders-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/70">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* ---------- Search box — filters by UPI ref, buyer email, phone, name, order ID, address, book title, notes ---------- */}
      <div className="relative mb-4 max-w-2xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by UPI ref · buyer email · phone · order ID · book title · address …"
          data-testid="orders-search-input"
          aria-label="Search orders"
          autoComplete="off"
          className="w-full pl-10 pr-10 py-2 rounded-lg bg-slate-900/60 border border-white/10 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-1 focus:ring-amber-300/60 focus:border-amber-300/40 transition"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            data-testid="orders-search-clear"
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-white/40 hover:text-white/80 hover:bg-white/10 transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {q && (
          <div
            data-testid="orders-search-result-count"
            className="mt-2 text-[11px] tracking-wide text-white/55 pl-1"
          >
            <span className="text-amber-300 font-semibold">{total}</span>
            {' '}of {totalRaw} orders match <span className="italic text-white/70">&ldquo;{query}&rdquo;</span>
            {total === 0 && (
              <span className="ml-2 text-white/40">— try the buyer&apos;s email, UPI reference or a short order-ID prefix.</span>
            )}
          </div>
        )}
      </div>

      {msg && <div className="text-xs text-rose-300 mb-3">{msg}</div>}

      {/* Stats ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
        {[
          { k: 'payment_pending', Icon: IndianRupee },
          { k: 'paid',            Icon: CheckCircle2 },
          { k: 'shipped',         Icon: Truck },
          { k: 'delivered',       Icon: Package },
          { k: 'cancelled',       Icon: Ban },
        ].map(({ k, Icon }) => {
          const meta = STATUS_META[k];
          return (
            <div key={k} className={`rounded-lg border ${meta.border} bg-slate-900/40 p-3`}>
              <Icon className={`w-3.5 h-3.5 ${meta.color} mb-1`} />
              <div className="text-xl font-serif font-bold text-white tabular-nums leading-none">{(buckets[k] || []).length}</div>
              <div className="text-[9px] tracking-wider uppercase text-white/50 mt-1">{meta.label}</div>
            </div>
          );
        })}
      </div>

      {busy ? (
        <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading orders…</div>
      ) : totalRaw === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-12 text-center">
          <ClipboardList className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/70 mb-1">No SAMANVAI orders yet.</div>
          <div className="text-[11px] text-white/50">Orders will appear here when buyers submit the &quot;Notify SAMANVAI of my order&quot; form.</div>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <div className="min-w-[1100px] grid grid-cols-5 gap-2 px-2">
            {['payment_pending', 'paid', 'shipped', 'delivered', 'cancelled'].map((k) => {
              const meta = STATUS_META[k];
              const list = filteredBuckets[k] || [];
              return (
                <div key={k} className="rounded-lg border border-white/10 bg-slate-900/40 p-2" data-testid={`orders-col-${k}`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${meta.color}`}>{meta.label}</div>
                    <span className="text-[10px] text-white/50 tabular-nums">{list.length}</span>
                  </div>
                  <div className="space-y-2">
                    {list.length === 0 ? (
                      <div className="text-[10px] text-white/40 italic px-1 py-3 text-center">{q ? 'No match' : 'Empty'}</div>
                    ) : list.map((o) => (
                      <OrderCard key={o.id} o={o} onAdvance={advance} onCancel={cancel} busy={pending} highlight={q} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

// -------------------------------------------------------------------
// Prospectus downloads · outbound signal dashboard
// -------------------------------------------------------------------
const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const ProspectusDownloadsView = () => {
  const [data, setData] = useState({ total: 0, count_24h: 0, count_7d: 0, top_recipients: [], results: [] });
  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('');

  const load = async () => {
    setBusy(true); setMsg('');
    try { const r = await popMBAListProspectusDownloads(300); setData(r); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Could not load downloads'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const q = filter.trim().toLowerCase();
  const rows = q
    ? (data.results || []).filter((r) =>
        (r.recipient || '').toLowerCase().includes(q)
        || (r.email || '').toLowerCase().includes(q)
        || (r.phone || '').toLowerCase().includes(q)
        || (r.ip || '').toLowerCase().includes(q)
        || (r.user_email || '').toLowerCase().includes(q))
    : (data.results || []);

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-white/60">
          Every personalised prospectus is logged as an outbound signal — follow up while interest is warm.
        </p>
        <button onClick={load} data-testid="prospectus-downloads-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/70">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {msg && <div className="text-xs text-rose-300 mb-3" data-testid="prospectus-downloads-error">{msg}</div>}

      {/* Stats ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {[
          { k: 'total',     label: 'All-time', value: data.total,     Icon: FileDown,   color: 'text-amber-300' },
          { k: '24h',       label: 'Last 24h', value: data.count_24h, Icon: Clock,      color: 'text-emerald-300' },
          { k: '7d',        label: 'Last 7d',  value: data.count_7d,  Icon: TrendingUp, color: 'text-sky-300' },
          { k: 'recip',     label: 'Unique recipients (top 10)', value: (data.top_recipients || []).length, Icon: Building2, color: 'text-white/80' },
        ].map((s) => (
          <div key={s.k} className="rounded-lg border border-white/10 bg-slate-900/40 p-3" data-testid={`prospectus-stat-${s.k}`}>
            <s.Icon className={`w-3.5 h-3.5 ${s.color} mb-1`} />
            <div className="text-xl font-serif font-bold text-white tabular-nums leading-none">{s.value}</div>
            <div className="text-[9px] tracking-wider uppercase text-white/50 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Top recipients */}
      {(data.top_recipients || []).length > 0 && (
        <div className="mb-6 rounded-xl border border-white/10 bg-slate-900/40 p-4">
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-3">TOP RECIPIENTS · WHERE TO FOLLOW UP FIRST</div>
          <div className="flex flex-wrap gap-2">
            {data.top_recipients.map((r) => (
              <div key={r.recipient} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-slate-950/60 border border-amber-300/20"
                data-testid={`prospectus-top-${r.recipient}`}>
                <Building2 className="w-3 h-3 text-amber-300" />
                <span className="text-xs text-white/90 font-medium">{r.recipient}</span>
                <span className="text-[10px] text-amber-300 tabular-nums font-bold">×{r.count}</span>
                <span className="text-[10px] text-white/40">· {relTime(r.last)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="mb-3">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by recipient, email, phone, IP…"
          data-testid="prospectus-downloads-filter"
          className="w-full sm:max-w-sm px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30"
        />
      </div>

      {busy ? (
        <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading downloads…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-12 text-center">
          <FileDown className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/70 mb-1">
            {data.total === 0 ? 'No prospectus downloads yet.' : 'No results for this filter.'}
          </div>
          <div className="text-[11px] text-white/50">
            Downloads appear here in real-time once someone personalises and downloads the AI-Enabled MBA prospectus.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="prospectus-downloads-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-3">Recipient</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">IP</th>
                <th className="text-left px-4 py-3">Signed-in user</th>
                <th className="text-left px-4 py-3">Referer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`prospectus-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
                      <span className="text-white/95 font-medium">{r.recipient}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.email && (
                      <a href={`mailto:${r.email}`} className="text-xs text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 leading-tight">
                        <Mail className="w-3 h-3 flex-shrink-0" /> {r.email}
                      </a>
                    )}
                    {r.phone && (
                      <a href={`tel:${r.phone}`} className="text-[11px] text-white/80 hover:text-white inline-flex items-center gap-1 leading-tight mt-1">
                        <Phone className="w-3 h-3 flex-shrink-0" /> {r.phone}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white/85 text-xs tabular-nums">{new Date(r.created_at).toLocaleString()}</div>
                    <div className="text-[10px] text-white/50 mt-0.5">{relTime(r.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1.5 text-xs text-white/75 tabular-nums">
                      <Globe className="w-3 h-3 text-white/40" /> {r.ip || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.user_email ? (
                      <>
                        <div className="text-xs text-white/85">{r.user_name || r.user_email}</div>
                        <div className="text-[10px] text-white/50">{r.user_email} · {roleLabel(r.user_role) || r.user_role}</div>
                      </>
                    ) : (
                      <span className="text-[10px] text-white/40 italic">Anonymous</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.referer
                      ? <a href={r.referer} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 max-w-[240px] truncate">
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{r.referer}</span>
                        </a>
                      : <span className="text-[10px] text-white/30">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};


// -------------------------------------------------------------------
// Advisor bookings · triage pipeline (new → contacted → scheduled → done/closed)
// -------------------------------------------------------------------
const ADVISOR_STATUS_META = {
  new:       { label: 'New',        color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  contacted: { label: 'Contacted',  color: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  scheduled: { label: 'Scheduled',  color: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  done:      { label: 'Done',       color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  closed:    { label: 'Closed',     color: 'bg-white/5 text-white/50 border-white/15' },
};
const ADVISOR_STATUS_ORDER = ['new', 'contacted', 'scheduled', 'done', 'closed'];

const _readableSlot = (s) => {
  const map = { 'next-business-day': 'Next business day', 'this-week': 'This week', 'next-week': 'Next week', 'weekend': 'Weekend' };
  return map[s] || s;
};

const _readableRole = (r) => {
  const map = {
    industry_expert:     'Industry expert',
    university_director: 'University / Dean',
    faculty:             'Faculty',
    student:             'Working professional',
    corporate:           'Corporate L&D',
    other:               'Other',
  };
  return map[r] || r;
};

const AdvisorBookingRow = ({ row, onStatusChange, expanded, onToggle }) => {
  const [noteDraft, setNoteDraft] = useState('');
  const [savingStatus, setSavingStatus] = useState(null);
  const meta = ADVISOR_STATUS_META[row.status] || ADVISOR_STATUS_META.new;

  const bumpTo = async (nextStatus) => {
    setSavingStatus(nextStatus);
    try {
      const r = await popUpdateAdvisorBookingStatus(row.id, nextStatus, noteDraft.trim());
      onStatusChange(r.booking);
      setNoteDraft('');
    } catch (_e) { /* surface via re-fetch */ }
    finally { setSavingStatus(null); }
  };

  return (
    <>
      <tr className="hover:bg-white/[0.02]" data-testid={`advisor-row-${row.id}`}>
        <td className="px-4 py-3">
          <div className="font-medium text-white/95">{row.name}</div>
          <div className="text-[10px] text-white/50 mt-0.5">{_readableRole(row.role)}</div>
        </td>
        <td className="px-4 py-3">
          {row.email && (
            <a href={`mailto:${row.email}`} className="text-xs text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 leading-tight">
              <Mail className="w-3 h-3 flex-shrink-0" /> {row.email}
            </a>
          )}
          {row.phone && (
            <a href={`tel:${row.phone}`} className="text-[11px] text-white/80 hover:text-white inline-flex items-center gap-1 leading-tight mt-1">
              <Phone className="w-3 h-3 flex-shrink-0" /> {row.phone}
            </a>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="text-white/85 text-xs tabular-nums">{new Date(row.created_at).toLocaleString()}</div>
          <div className="text-[10px] text-white/50 mt-0.5">{relTime(row.created_at)}</div>
        </td>
        <td className="px-4 py-3">
          <div className="inline-flex items-center gap-1.5 text-xs text-white/80"><CalendarIcon className="w-3 h-3 text-white/40" /> {_readableSlot(row.slot)}</div>
          <div className="text-[10px] text-white/50 mt-0.5 uppercase tracking-wider">{row.program === 'ai-mba' ? 'AI-Enabled MBA' : (row.program || 'General')}</div>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.color}`}>
            {meta.label}
          </span>
        </td>
        <td className="px-4 py-3">
          <button onClick={onToggle}
            data-testid={`advisor-row-toggle-${row.id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/85">
            {expanded ? 'Hide' : 'Triage'} <ArrowRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-4 pb-5 pt-2 bg-white/[0.02]" data-testid={`advisor-row-triage-${row.id}`}>
            {row.notes && (
              <div className="mb-3 rounded-md bg-slate-950/60 border border-white/10 px-3 py-2">
                <div className="text-[9px] tracking-widest font-bold text-amber-300 mb-1">USER NOTE</div>
                <div className="text-[12px] text-white/80 italic">&ldquo;{row.notes}&rdquo;</div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {ADVISOR_STATUS_ORDER.map((s) => {
                const m = ADVISOR_STATUS_META[s];
                const current = row.status === s;
                const saving = savingStatus === s;
                return (
                  <button key={s} onClick={() => bumpTo(s)} disabled={current || saving}
                    data-testid={`advisor-status-btn-${row.id}-${s}`}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-semibold border transition ${
                      current
                        ? 'bg-white/10 text-white/50 border-white/15 cursor-default'
                        : `hover:bg-white/5 ${m.color}`
                    }`}>
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Move to {m.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <input type="text" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value.slice(0, 500))}
                data-testid={`advisor-note-input-${row.id}`}
                placeholder="Add a triage note (optional — captured with the next status change)…"
                className="flex-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-xs text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
              <span className="text-[10px] text-white/40 tabular-nums">{noteDraft.length}/500</span>
            </div>

            {Array.isArray(row.admin_notes) && row.admin_notes.length > 0 && (
              <div className="mt-3">
                <div className="text-[9px] tracking-widest font-bold text-white/40 mb-2">TRIAGE HISTORY</div>
                <div className="space-y-1.5">
                  {[...row.admin_notes].reverse().map((n, i) => (
                    <div key={i} className="text-[11px] text-white/70 flex items-start gap-2">
                      <MessageSquare className="w-3 h-3 mt-0.5 text-white/30 flex-shrink-0" />
                      <span>
                        <span className="text-white/90">{n.note}</span>
                        <span className="text-white/40"> · {n.by_name || n.by || 'admin'} · {relTime(n.at || n.created_at)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AdvisorBookingsView = () => {
  const [data, setData] = useState({ total: 0, count_24h: 0, buckets: {}, results: [] });
  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setBusy(true); setMsg('');
    try { const r = await popListAdvisorBookings(300); setData(r); }
    catch (e) { setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Could not load bookings'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const q = filter.trim().toLowerCase();
  const rows = (data.results || []).filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (!q) return true;
    return (r.name || '').toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q)
      || (r.phone || '').toLowerCase().includes(q)
      || (r.ip || '').toLowerCase().includes(q)
      || (r.role || '').toLowerCase().includes(q);
  });

  const replaceRow = (updated) => {
    setData((prev) => ({
      ...prev,
      results: (prev.results || []).map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
    }));
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-white/60">
          Every &ldquo;Talk to a program advisor&rdquo; request lands here. Move each lead through the pipeline so nothing goes stale.
        </p>
        <button onClick={load} data-testid="advisor-bookings-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/70">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {msg && <div className="text-xs text-rose-300 mb-3" data-testid="advisor-bookings-error">{msg}</div>}

      {/* Stats ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-6">
        <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3" data-testid="advisor-stat-total">
          <Headphones className="w-3.5 h-3.5 text-amber-300 mb-1" />
          <div className="text-xl font-serif font-bold text-white tabular-nums leading-none">{data.total}</div>
          <div className="text-[9px] tracking-wider uppercase text-white/50 mt-1">All-time</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3" data-testid="advisor-stat-24h">
          <Clock className="w-3.5 h-3.5 text-emerald-300 mb-1" />
          <div className="text-xl font-serif font-bold text-white tabular-nums leading-none">{data.count_24h}</div>
          <div className="text-[9px] tracking-wider uppercase text-white/50 mt-1">Last 24h</div>
        </div>
        {ADVISOR_STATUS_ORDER.map((s) => (
          <div key={s} className="rounded-lg border border-white/10 bg-slate-900/40 p-3" data-testid={`advisor-stat-${s}`}>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${ADVISOR_STATUS_META[s].color}`}>
              {ADVISOR_STATUS_META[s].label}
            </span>
            <div className="text-xl font-serif font-bold text-white tabular-nums leading-none mt-1.5">{data.buckets?.[s] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name, email, phone, IP, role…"
          data-testid="advisor-bookings-filter"
          className="flex-1 min-w-[220px] px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30"
        />
        <div className="flex flex-wrap gap-1">
          {['all', ...ADVISOR_STATUS_ORDER].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              data-testid={`advisor-status-filter-${s}`}
              className={`px-2.5 py-1.5 rounded-md text-[10px] tracking-wider font-bold uppercase transition ${
                statusFilter === s
                  ? 'bg-amber-300 text-slate-900'
                  : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
              }`}>
              {s === 'all' ? 'All' : ADVISOR_STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      {busy ? (
        <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading bookings…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-12 text-center">
          <Headphones className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/70 mb-1">
            {data.total === 0 ? 'No advisor bookings yet.' : 'No results for this filter.'}
          </div>
          <div className="text-[11px] text-white/50">
            The &ldquo;Talk to a program advisor&rdquo; FAB on /pop and /pop/ai-mba lands leads here in real time.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="advisor-bookings-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-3">Requester</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Preferred slot · Program</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => (
                <AdvisorBookingRow
                  key={r.id}
                  row={r}
                  expanded={expandedId === r.id}
                  onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  onStatusChange={replaceRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};


// -------------------------------------------------------------------
// Page
// -------------------------------------------------------------------
const PoPAdmin = () => {
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('verification');

  useEffect(() => {
    if (!loading && (!user || user.role !== 'super_admin')) navigate('/pop', { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) return (<PoPShell><div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div></PoPShell>);

  return (
    <PoPShell activeTab="admin">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">SUPER ADMIN</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-1">Governance Console</h1>
      </div>

      <div className="flex gap-1.5 mb-6 border-b border-white/10 pb-3 flex-wrap">
        {[
          { id: 'verification',           label: 'Verification queue',    Icon: ShieldCheck },
          { id: 'orders',                 label: 'SAMANVAI book orders',  Icon: ClipboardList },
          { id: 'advisor-bookings',       label: 'Advisor bookings',      Icon: Headphones },
          { id: 'prospectus-downloads',   label: 'Prospectus downloads',  Icon: FileDown },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold transition ${
              tab === t.id ? 'bg-amber-300 text-slate-900' : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
            }`}>
            <t.Icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'verification'         && <VerificationView />}
      {tab === 'orders'               && <OrdersView />}
      {tab === 'advisor-bookings'     && <AdvisorBookingsView />}
      {tab === 'prospectus-downloads' && <ProspectusDownloadsView />}
    </PoPShell>
  );
};

export default PoPAdmin;
